import React, { useEffect, useContext } from 'react';
import Styled from 'styled';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { UserStore } from 'four-nets/user/user';
import { useLazyQuery, useMutation } from '@apollo/client/react/hooks';
import { Field, Form, Formik, FormikProps, FieldProps } from 'formik';

import Input from 'four-nets/ui-v2/form/input/input';
import { AlertContext } from 'lib/alert-context/alert-context';
import Modal from 'four-nets/ui-v2/modal/modal';

import defaultStyles from './password-dialog.scss';
import query, { CheckPasswordQuery, CheckPasswordQueryVariables } from './check-password.graphql';
import mutation, { ChangePasswordMutation, ChangePasswordMutationVariables } from './mutation.graphql';

interface Props {
    styles: typeof defaultStyles;
    onClose: () => void;
    id: string;
    username: string;
    open: boolean;
}

interface Values {
    oldPassword: string;
    newPassword: string;
    newPasswordRepeat: string;
}

const PasswordDialog: React.FC<Props> = (props, context) => {
    const { styles, id, username, open, onClose } = props;

    let formikValidateField: null | ((field: string) => void) = null;

    const [checkPassword, { data }] = useLazyQuery<CheckPasswordQuery, CheckPasswordQueryVariables>(query);
    const [updatePassword] = useMutation<ChangePasswordMutation, ChangePasswordMutationVariables>(mutation);

    const { fail } = useContext(AlertContext);

    useEffect(() => {
        if (formikValidateField) {
            formikValidateField('oldPassword');
        }
    }, [data]);

    return (
        <React.Fragment>
            <Modal title={<FormattedMessage id='CHANGE_PASSWORD' />} onClose={onClose} open={open}>
                <Formik
                    initialValues={{
                        oldPassword: '',
                        newPassword: '',
                        newPasswordRepeat: '',
                    }}
                    onSubmit={values => {
                        updatePassword({
                            variables: {
                                input: {
                                    id,
                                    oldPassword: values.oldPassword,
                                    newPassword: values.newPassword,
                                },
                            },
                        })
                            .then(() => {
                                onClose();
                            })
                            .catch(error => {
                                fail!({
                                    body: <FormattedMessage id={error.message} />,
                                    onAccept: () => window.location.reload(true),
                                });
                            });
                    }}
                    validate={(values: Values) => {
                        const errors = {} as any;

                        if (values.newPassword.length < 3) {
                            errors.newPassword = [
                                <FormattedMessage id='PASSWORD_MINIMUM_LENGTH' values={{ length: 3 }} />,
                            ];
                        }
                        if (values.newPassword === username) {
                            errors.newPassword = [<FormattedMessage id='USERNAME_AND_PASSWORD_CANNOT_BE_SAME' />];
                        }

                        if (values.newPassword !== values.newPasswordRepeat) {
                            errors.newPasswordRepeat = [<FormattedMessage id='PASSWORD_FIELDS_DID_NOT_MATCH.' />];
                        }

                        return errors;
                    }}
                    render={({ isValid, isSubmitting, validateField }: FormikProps<Values>) => {
                        formikValidateField = validateField;
                        return (
                            <Form>
                                <label>
                                    <FormattedMessage id='CURRENT_PASSWORD' />
                                    <Field
                                        name='oldPassword'
                                        validate={(value: string) => {
                                            if (value === '') {
                                                return [<FormattedMessage id='API_INCORRECT_PASSWORD' />];
                                            }
                                            if (data && data.checkPassword) {
                                                if (data.checkPassword.correctPassword) {
                                                    return null;
                                                } else {
                                                    return [<FormattedMessage id='API_INCORRECT_PASSWORD' />];
                                                }
                                            }
                                            return null;
                                        }}
                                    >
                                        {({ field, form: { errors, validateField } }: FieldProps) => (
                                            <Input
                                                {...field}
                                                type='password'
                                                errors={errors[field.name]}
                                                touched={true}
                                                onBlur={() => {
                                                    checkPassword({ variables: { id, password: field.value } });
                                                }}
                                            />
                                        )}
                                    </Field>
                                </label>
                                <label>
                                    <FormattedMessage id='NEW_PASSWORD' />
                                    <Field name='newPassword'>
                                        {({ field, form: { errors, touched } }: FieldProps) => (
                                            <Input
                                                {...field}
                                                errors={errors[field.name]}
                                                touched={touched[field.name]}
                                                type='password'
                                            />
                                        )}
                                    </Field>
                                </label>
                                <label>
                                    <FormattedMessage id='CONFIRM_NEW_PASSWORD' />
                                    <Field name='newPasswordRepeat'>
                                        {({ field, form: { errors, touched } }: FieldProps) => (
                                            <Input
                                                {...field}
                                                errors={errors[field.name]}
                                                touched={touched[field.name]}
                                                type='password'
                                            />
                                        )}
                                    </Field>
                                </label>
                                <button
                                    disabled={!isValid || isSubmitting}
                                    type='submit'
                                    className={styles.submitButton}
                                >
                                    <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                                </button>
                            </Form>
                        );
                    }}
                />
            </Modal>
        </React.Fragment>
    );
};

PasswordDialog.contextTypes = {
    userStore: PropTypes.instanceOf(UserStore),
};

export default Styled(defaultStyles)(PasswordDialog);
